import { Box, Button, Stack, Typography } from "@mui/material";
import EastIcon from "@mui/icons-material/East";
import { Link } from "react-router";
import { useLayoutEffect, useRef, useState } from "react";

type StickyContentChild = {
  title: string;
  description: string;
  buttonText: string;
  to: string;
};

export type StickyContentProps = {
  title: string;
  subTitle: string;
  description?: string;
  childContent: StickyContentChild[];
};

interface StickyContentExtendedProps extends StickyContentProps {
  scrollPosition: number;
}

export const StickyContent = ({
  title,
  subTitle,
  description,
  childContent,
  scrollPosition,
}: StickyContentExtendedProps) => {
  const [offsetTop, setOffsetTop] = useState<number | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const padding = window.innerHeight / 3;
    if (contentRef.current) {
      setOffsetTop(contentRef.current.offsetTop - padding);
    }
  }, []);

  const isVisible = offsetTop && scrollPosition >= offsetTop;

  return (
    <Box
      component="section"
      className="sticky-content"
      sx={{
        pt: 8,
        pb: 8,
        opacity: isVisible ? 1 : 0,
        transition: "opacity 0.3s ease-in-out",
      }}
      ref={contentRef}
    >
      <Typography variant="overline" className="text-purple">
        {subTitle}
      </Typography>

      <Typography
        variant="h4"
        className="font-body font-size-4"
        fontWeight={600}
        sx={{ mb: !!description ? 0 : "36px" }}
      >
        {title}
      </Typography>
      {description && (
        <Typography sx={{ mb: "36px" }} variant="h6" fontWeight={400} className="font-size-3">
          {description}
        </Typography>
      )}
      <Stack spacing={3}>
        {childContent.map((child) => (
          <Box key={child.title}>
            <Typography
              variant="h6"
              className="font-body font-size-4 text-pink"
              fontWeight={600}
              mb="8px"
            >
              {child.title}
            </Typography>

            <Typography variant="body1" className="font-size-3" mb="20px">
              {child.description}
            </Typography>

            <Button
              component={Link}
              to={child.to}
              color={"purple" as any}
              variant="text"
              endIcon={<EastIcon />}
            >
              <Typography variant="body1" component="span" fontWeight={400}>
                {child.buttonText}
              </Typography>
            </Button>
          </Box>
        ))}
      </Stack>
    </Box>
  );
};
